/**
 * CivicPulse — Leaflet map with colored pins. Category colors come
 * from the taxonomy the backend returns. Pins are pure DivIcons so we
 * don't ship image tiles.
 */
import { useEffect, useMemo, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";

const CENTER_KERALA = [10.0261, 76.3125]; // Kochi
const DEFAULT_ZOOM = 8;

function pinIcon(color = "#0F7B3F") {
  const html = `
    <div style="position:relative;width:32px;height:38px;filter:drop-shadow(0 2px 3px rgba(0,0,0,.25));">
      <svg viewBox="0 0 32 38" width="32" height="38">
        <path d="M16 0 C7 0 0 7 0 16 c0 10 16 22 16 22 s16-12 16-22 c0-9-7-16-16-16z"
              fill="${color}" stroke="#fff" stroke-width="2"/>
        <circle cx="16" cy="15" r="6" fill="#fff"/>
      </svg>
    </div>`;
  return L.divIcon({
    html,
    className: "civic-pin",
    iconSize: [32, 38],
    iconAnchor: [16, 38],
    popupAnchor: [0, -32],
  });
}

export default function CivicMap({
  issues = [],
  categoryColors = {},
  height = 480,
  center = CENTER_KERALA,
  zoom = DEFAULT_ZOOM,
  interactive = true,
  onPickLocation,        // (lat,lng) => void — enables click-to-pin
  pickedLocation,        // {lat,lng} — the marker shown for the report wizard
  fitToIssues = true,
}) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const markerLayerRef = useRef(null);
  const pickedMarkerRef = useRef(null);

  // Initialize once.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center,
      zoom,
      zoomControl: interactive,
      dragging: interactive,
      scrollWheelZoom: interactive,
      doubleClickZoom: interactive,
      touchZoom: interactive,
      boxZoom: interactive,
      keyboard: interactive,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map);
    markerLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Attach click handler when in "pick" mode.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!onPickLocation) return;
    const handler = (e) => onPickLocation(e.latlng.lat, e.latlng.lng);
    map.on("click", handler);
    return () => map.off("click", handler);
  }, [onPickLocation]);

  // Render issue pins.
  useEffect(() => {
    const map = mapRef.current;
    const layer = markerLayerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    issues.forEach((it) => {
      if (!it?.location?.lat || !it?.location?.lng) return;
      const color = categoryColors[it.category] || "#0F7B3F";
      const m = L.marker([it.location.lat, it.location.lng], { icon: pinIcon(color) });
      const html = `
        <div style="min-width:180px;font-family:system-ui,sans-serif">
          <div style="font-weight:600;color:#0F7B3F;font-size:11px;text-transform:uppercase;letter-spacing:.06em">${it.category}</div>
          <div style="font-weight:600;margin-top:2px;color:#111">${escapeHtml(it.title)}</div>
          <div style="color:#555;font-size:12px;margin-top:2px">${escapeHtml(it.location.address || "")}</div>
          <a href="/civic/issue/${encodeURIComponent(it.public_id)}"
             style="display:inline-block;margin-top:8px;color:#0F7B3F;font-weight:600;font-size:12px;text-decoration:none">
             View →
          </a>
        </div>`;
      m.bindPopup(html);
      m.addTo(layer);
    });
    if (fitToIssues && issues.length > 0) {
      const bounds = L.latLngBounds(
        issues.filter((i) => i?.location?.lat).map((i) => [i.location.lat, i.location.lng])
      );
      if (bounds.isValid()) map.fitBounds(bounds.pad(0.2), { animate: false, maxZoom: 13 });
    }
  }, [issues, categoryColors, fitToIssues]);

  // Picked-location marker (for report wizard).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (pickedMarkerRef.current) {
      pickedMarkerRef.current.remove();
      pickedMarkerRef.current = null;
    }
    if (pickedLocation?.lat && pickedLocation?.lng) {
      const m = L.marker([pickedLocation.lat, pickedLocation.lng], {
        icon: pinIcon("#0B2545"),
        draggable: Boolean(onPickLocation),
      }).addTo(map);
      if (onPickLocation) {
        m.on("dragend", (e) => {
          const { lat, lng } = e.target.getLatLng();
          onPickLocation(lat, lng);
        });
      }
      pickedMarkerRef.current = m;
      map.setView([pickedLocation.lat, pickedLocation.lng], Math.max(map.getZoom(), 14), { animate: true });
    }
  }, [pickedLocation, onPickLocation]);

  return (
    <div
      ref={containerRef}
      className="rounded-2xl overflow-hidden ring-1 ring-emerald-100"
      style={{ height }}
      data-testid="civic-map"
    />
  );
}

function escapeHtml(s = "") {
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  }[c]));
}

export function MapLegend({ categories = [], counters = {} }) {
  const top = useMemo(
    () => [...categories]
      .map((c) => ({ ...c, count: counters[c.slug] || 0 }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5),
    [categories, counters]
  );
  return (
    <div className="rounded-2xl bg-white ring-1 ring-emerald-100 p-4 shadow-sm">
      <div className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
        <span className="text-emerald-700">◐</span> Map Legend
      </div>
      <ul className="space-y-1.5 text-sm">
        {top.map((c) => (
          <li key={c.slug} className="flex items-center gap-2">
            <span
              className="w-4 h-4 rounded-full ring-2 ring-white shadow"
              style={{ background: c.color }}
            />
            <span className="text-slate-700">{c.label_en}</span>
            <span className="ml-auto text-slate-400 tabular-nums text-xs">{c.count}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
